import React, { useEffect, useState } from 'react';

const API = 'https://gridmind-production-9082.up.railway.app';

// SVG layout coordinates — unchanged (visual positions only)
const STATE_COORDS = {
  "Rajasthan":      { x: 285, y: 180, w: 105, h: 120 },
  "Gujarat":        { x: 195, y: 240, w: 90,  h: 100 },
  "Maharashtra":    { x: 265, y: 295, w: 140, h: 100 },
  "MP":             { x: 335, y: 210, w: 120, h: 90  },
  "UP":             { x: 355, y: 148, w: 145, h: 70  },
  "Tamil Nadu":     { x: 318, y: 450, w: 80,  h: 85  },
  "Karnataka":      { x: 282, y: 368, w: 110, h: 90  },
  "Andhra Pradesh": { x: 372, y: 355, w: 110, h: 95  },
  "Telangana":      { x: 368, y: 318, w: 90,  h: 60  },
  "Punjab":         { x: 300, y: 100, w: 80,  h: 50  },
  "Bihar":          { x: 478, y: 182, w: 80,  h: 55  },
  "West Bengal":    { x: 535, y: 192, w: 60,  h: 88  },
  "Odisha":         { x: 475, y: 268, w: 85,  h: 75  },
  "Kerala":         { x: 278, y: 445, w: 48,  h: 70  },
  "Jharkhand":      { x: 475, y: 228, w: 80,  h: 55  },
};

function getColor(curtailment) {
  if (curtailment > 15) return '#FF5252';
  if (curtailment > 5)  return '#FFB300';
  if (curtailment > 0)  return '#00E676';
  return '#1E4A6E';
}

export default function IndiaMap() {
  const [selected, setSelected]   = useState(null);
  const [metric, setMetric]       = useState('curtailment');
  const [hovered, setHovered]     = useState(null);
  const [stateData, setStateData] = useState({});
  const [loading, setLoading]     = useState(true);
  const [dataSource, setDataSource] = useState('');

  // Fetch real state data from server
  useEffect(() => {
    fetch(`${API}/api/states`)
      .then(r => r.json())
      .then(data => {
        setStateData(data);
        setDataSource('CEA Annual Report 2024-25 + MNRE');
        setLoading(false);
      })
      .catch(() => {
        // Fallback — real CEA figures if API fails
        setStateData({
          "Rajasthan":      { solar_gw: 22.4, wind_gw: 6.1,  demand_gw: 9.1,  curtailment_est_pct: 18, thermal_gw: 8.2  },
          "Gujarat":        { solar_gw: 14.2, wind_gw: 9.8,  demand_gw: 19.8, curtailment_est_pct: 7,  thermal_gw: 21.4 },
          "Maharashtra":    { solar_gw: 9.8,  wind_gw: 7.4,  demand_gw: 29.2, curtailment_est_pct: 4,  thermal_gw: 24.8 },
          "MP":             { solar_gw: 7.8,  wind_gw: 3.2,  demand_gw: 13.1, curtailment_est_pct: 3,  thermal_gw: 14.2 },
          "UP":             { solar_gw: 5.2,  wind_gw: 0.6,  demand_gw: 24.8, curtailment_est_pct: 1,  thermal_gw: 18.4 },
          "Tamil Nadu":     { solar_gw: 7.4,  wind_gw: 12.1, demand_gw: 17.4, curtailment_est_pct: 14, thermal_gw: 9.8  },
          "Karnataka":      { solar_gw: 9.2,  wind_gw: 6.8,  demand_gw: 18.2, curtailment_est_pct: 11, thermal_gw: 7.4  },
          "Andhra Pradesh": { solar_gw: 7.8,  wind_gw: 5.2,  demand_gw: 13.8, curtailment_est_pct: 8,  thermal_gw: 6.2  },
          "Telangana":      { solar_gw: 5.4,  wind_gw: 2.1,  demand_gw: 11.2, curtailment_est_pct: 3,  thermal_gw: 8.8  },
          "Punjab":         { solar_gw: 2.1,  wind_gw: 0.9,  demand_gw: 9.1,  curtailment_est_pct: 0,  thermal_gw: 6.4  },
          "Bihar":          { solar_gw: 1.2,  wind_gw: 0.1,  demand_gw: 6.8,  curtailment_est_pct: 0,  thermal_gw: 3.2  },
          "West Bengal":    { solar_gw: 1.8,  wind_gw: 0.2,  demand_gw: 14.2, curtailment_est_pct: 0,  thermal_gw: 8.4  },
          "Odisha":         { solar_gw: 3.2,  wind_gw: 0.9,  demand_gw: 5.8,  curtailment_est_pct: 2,  thermal_gw: 6.8  },
          "Kerala":         { solar_gw: 1.1,  wind_gw: 0.2,  demand_gw: 6.4,  curtailment_est_pct: 0,  thermal_gw: 0.8  },
          "Jharkhand":      { solar_gw: 1.4,  wind_gw: 0.1,  demand_gw: 4.8,  curtailment_est_pct: 0,  thermal_gw: 4.8  },
        });
        setDataSource('CEA Annual Report 2024-25 (local fallback)');
        setLoading(false);
      });
  }, []);

  // Helper — works with both field naming conventions
  // Server returns { solar, wind, demand, curtailment }
  // Fallback uses { solar_gw, wind_gw, demand_gw, curtailment_est_pct }
  const getSolar      = (d) => d?.solar_gw      ?? d?.solar      ?? 0;
  const getWind       = (d) => d?.wind_gw       ?? d?.wind       ?? 0;
  const getDemand     = (d) => d?.demand_gw     ?? d?.demand     ?? 0;
  const getCurtailment= (d) => d?.curtailment_est_pct ?? d?.curtailment ?? 0;

  const getValue = (name) => {
    const d = stateData[name];
    if (!d) return '—';
    if (metric === 'curtailment') return `${getCurtailment(d)}%`;
    if (metric === 'solar')       return `${getSolar(d)}GW`;
    return `${getDemand(d)}GW`;
  };

  const getMetricColor = (name) => {
    const d = stateData[name];
    if (!d) return '#1E4A6E';
    const solar = getSolar(d);
    const demand = getDemand(d);
    const curtailment = getCurtailment(d);
    if (metric === 'curtailment') return getColor(curtailment);
    if (metric === 'solar')       return solar > 10 ? '#00E676' : solar > 5 ? '#4FC3F7' : '#1E4A6E';
    return demand > 20 ? '#FF5252' : demand > 10 ? '#FFB300' : '#00E676';
  };

  const sel = selected ? stateData[selected] : null;

  // Summary stats
  const getSolar2      = (d) => d?.solar_gw ?? d?.solar ?? 0;
  const getCurtail2    = (d) => d?.curtailment_est_pct ?? d?.curtailment ?? 0;
  const totalSolar     = Object.values(stateData).reduce((s,d) => s + getSolar2(d), 0).toFixed(0);
  const highCurtail    = Object.values(stateData).filter(d => getCurtail2(d) > 15).length;
  const totalCurtail   = Object.entries(stateData)
    .filter(([,d]) => getCurtail2(d) > 0)
    .reduce((s,[,d]) => s + getSolar2(d) * (getCurtail2(d) / 100), 0).toFixed(1);

  return (
    <div style={{ padding: '24px', color: '#EEF4F0', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>
          India <span style={{ color: '#00E676' }}>Grid Map</span>
        </h1>
        <p style={{ color: '#6B8FA3', fontSize: 13, marginTop: 4 }}>
          State-wise grid intelligence · Real CEA data ·
          <span style={{ color: '#00E676', marginLeft: 6 }}>
            {loading ? 'Loading...' : dataSource}
          </span>
        </p>
        <p style={{ color: '#6B8FA3', fontSize: 11, marginTop: 6, fontFamily: 'IBM Plex Mono, monospace' }}>
          Static — CEA Annual Report 2024-25, not live telemetry.
        </p>
      </div>

      {/* Metric selector */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          { key: 'curtailment', label: '⚡ Curtailment Risk' },
          { key: 'solar',       label: '☀️ Solar Capacity'   },
          { key: 'load',        label: '🔋 Grid Demand'       },
        ].map(m => (
          <button key={m.key} onClick={() => setMetric(m.key)} style={{
            padding: '6px 14px', fontSize: 12, fontFamily: 'IBM Plex Mono, monospace',
            background: metric === m.key ? '#00E676' : '#0F2040',
            color: metric === m.key ? '#0A1628' : '#6B8FA3',
            border: `1px solid ${metric === m.key ? '#00E676' : '#1E3A5F'}`,
            borderRadius: 4, cursor: 'pointer', fontWeight: metric === m.key ? 700 : 400,
          }}>{m.label}</button>
        ))}
      </div>

      {/* Stats cards — real CEA data */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'High Curtailment States', val: highCurtail,      sub: 'Curtailment > 15%',          color: '#FF5252' },
          { label: 'Total Solar Installed',   val: `${totalSolar} GW`, sub: 'CEA Annual Report 2024-25', color: '#00E676' },
          { label: 'Estimated Curtailed',     val: `${totalCurtail} GW`, sub: 'Daily average estimate', color: '#FFB300' },
          { label: 'States Monitored',        val: Object.keys(stateData).length, sub: 'Real CEA data', color: '#4FC3F7' },
        ].map((c, i) => (
          <div key={i} style={{
            background: '#0F2040', border: `1px solid ${c.color}33`,
            borderRadius: 8, padding: '14px 16px', borderTop: `3px solid ${c.color}`
          }}>
            <div style={{ fontSize: 10, color: '#6B8FA3', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: c.color, lineHeight: 1 }}>{c.val}</div>
            <div style={{ fontSize: 11, color: '#6B8FA3', marginTop: 6 }}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Map + Detail */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 16 }}>

        {/* SVG Map */}
        <div style={{ background: '#0F2040', border: '1px solid #1E3A5F', borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 12, color: '#00E676', fontFamily: 'IBM Plex Mono', marginBottom: 12 }}>
            INDIA GRID MAP — {dataSource || 'Loading...'}
          </div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
            {[['#FF5252','High (>15%)'],['#FFB300','Medium (5-15%)'],['#00E676','Low (<5%)'],['#1E4A6E','Minimal']].map(([c,l]) => (
              <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 10, height: 10, background: c, borderRadius: 2 }} />
                <span style={{ fontSize: 10, color: '#6B8FA3' }}>{l}</span>
              </div>
            ))}
          </div>

          {loading ? (
            <div style={{ height: 380, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B8FA3' }}>
              Loading real CEA state data...
            </div>
          ) : (
            <svg viewBox="170 80 430 430" style={{ width: '100%', height: 380 }}>
              {Object.entries(STATE_COORDS).map(([name, coords]) => {
                const color = getMetricColor(name);
                const isSelected = selected === name;
                const isHovered  = hovered  === name;
                return (
                  <g key={name}>
                    <rect
                      x={coords.x} y={coords.y} width={coords.w} height={coords.h}
                      rx={4}
                      fill={color}
                      fillOpacity={isSelected ? 1 : isHovered ? 0.85 : 0.65}
                      stroke={isSelected ? '#FFFFFF' : '#0A1628'}
                      strokeWidth={isSelected ? 2.5 : 1}
                      style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                      onClick={() => setSelected(selected === name ? null : name)}
                      onMouseEnter={() => setHovered(name)}
                      onMouseLeave={() => setHovered(null)}
                    />
                    <text
                      x={coords.x + coords.w/2} y={coords.y + coords.h/2 - 6}
                      textAnchor="middle" fontSize="8"
                      fill="rgba(255,255,255,0.9)"
                      style={{ pointerEvents: 'none', fontFamily: 'IBM Plex Mono' }}
                    >
                      {name.length > 10 ? name.split(' ')[0] : name}
                    </text>
                    <text
                      x={coords.x + coords.w/2} y={coords.y + coords.h/2 + 8}
                      textAnchor="middle" fontSize="9"
                      fill="white" fontWeight="700"
                      style={{ pointerEvents: 'none', fontFamily: 'IBM Plex Mono' }}
                    >
                      {getValue(name)}
                    </text>
                  </g>
                );
              })}
            </svg>
          )}
          <div style={{ fontSize: 10, color: '#6B8FA3', textAlign: 'center', marginTop: 8 }}>
            Click any state for detailed AI analysis · Source: {dataSource}
          </div>
        </div>

        {/* Detail panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sel && selected && (() => {
            const solar = getSolar(sel);
            const wind = getWind(sel);
            const demand = getDemand(sel);
            const curtailment = getCurtailment(sel);
            return (
              <div style={{ background: '#0F2040', border: '1px solid #1E3A5F', borderRadius: 8, padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{selected}</div>
                  <span style={{
                    fontSize: 10, padding: '3px 10px', borderRadius: 4,
                    background: curtailment > 15 ? '#FF525233' : curtailment > 5 ? '#FFB30033' : '#00E67633',
                    color: curtailment > 15 ? '#FF5252' : curtailment > 5 ? '#FFB300' : '#00E676',
                  }}>
                    {curtailment > 15 ? 'CRITICAL' : curtailment > 5 ? 'MODERATE' : 'NORMAL'}
                  </span>
                </div>
                {[
                  { label: 'Solar Installed',  val: solar,       max: 25, unit: 'GW', color: '#00E676' },
                  { label: 'Wind Installed',   val: wind,        max: 15, unit: 'GW', color: '#4FC3F7' },
                  { label: 'Grid Demand',      val: demand,      max: 35, unit: 'GW', color: '#FFB300' },
                  { label: 'Curtailment Est.', val: curtailment, max: 30, unit: '%',  color: curtailment > 15 ? '#FF5252' : '#FFB300' },
                ].map(b => (
                  <div key={b.label} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: '#6B8FA3' }}>{b.label}</span>
                      <span style={{ fontSize: 11, color: b.color, fontFamily: 'IBM Plex Mono' }}>{b.val} {b.unit}</span>
                    </div>
                    <div style={{ height: 4, background: '#1E3A5F', borderRadius: 2 }}>
                      <div style={{ width: `${Math.min(100, b.val/b.max*100)}%`, height: '100%', background: b.color, borderRadius: 2, transition: 'width 0.5s' }} />
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: 4, fontSize: 10, color: '#6B8FA3', fontFamily: 'IBM Plex Mono' }}>
                  Source: CEA Annual Report 2024-25 + MNRE
                </div>
              </div>
            );
          })()}
            <div style={{ background: '#0F2040', border: '1px solid #1E3A5F', borderRadius: 8, padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🗺️</div>
              <div style={{ fontSize: 14, color: '#6B8FA3' }}>Click any state on the map</div>
              <div style={{ fontSize: 11, color: '#1E3A5F', marginTop: 8 }}>to see real CEA generation data and AI analysis</div>
            </div>

          {/* Critical states — sorted by curtailment */}
          <div style={{ background: '#0F2040', border: '1px solid #1E3A5F', borderRadius: 8, padding: 16 }}>
            <div style={{ fontSize: 11, color: '#FF5252', fontFamily: 'IBM Plex Mono', marginBottom: 12, letterSpacing: 2 }}>
              CRITICAL STATES — CEA DATA
            </div>
            {Object.entries(stateData)
              .filter(([,d]) => getCurtail2(d) > 0)
              .sort(([,a],[,b]) => getCurtail2(b) - getCurtail2(a))
              .slice(0, 6)
              .map(([name, d]) => (
                <div key={name} onClick={() => setSelected(name)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #1E3A5F', cursor: 'pointer' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: getCurtail2(d) > 15 ? '#FF5252' : getCurtail2(d) > 5 ? '#FFB300' : '#00E676' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: '#EEF4F0' }}>{name}</div>
                    <div style={{ fontSize: 10, color: '#6B8FA3' }}>
                      Solar: {getSolar2(d)}GW · Demand: {(d.demand_gw ?? d.demand ?? 0)}GW
                    </div>
                  </div>
                  <div style={{ fontSize: 11, fontFamily: 'IBM Plex Mono',
                    color: getCurtail2(d) > 15 ? '#FF5252' : '#FFB300' }}>
                    {getCurtail2(d)}%
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
